import { useApolloClient } from '@apollo/react-hooks';
import { gqlTagSwag } from '@atlassian/jira-apollo-swag';
import { useEpicsStoreActions } from '@atlassian/jira-backlog-epics-store';
import { fg } from '@atlassian/jira-feature-gating';
import type { EpicQueryResponse } from '@atlassian/jira-software-backlog-panel';
import { epicFragment } from '@atlassian/jira-software-backlog-panel/src/common/graphql/fragments';
import type { EpicQueryVariables } from '../../../model/fragment/epic/types';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities';
import type { UpdateEpicSummary } from './type';

// We need to pass in isCMP here as that is what is used to get the data originally and
// Apollo needs that info to get the corrent fragment
export const EPIC_QUERY = gqlTagSwag`
    ${epicFragment}
    query BacklogEpicsCache($boardId: ID!, $isCompanyManaged: Boolean!) {
        boardScope(boardId: $boardId, isCMP: $isCompanyManaged) {
            issueParents {
                ...EpicPanelIssueParentFragment
            }
        }
    }
`;

export const useUpdateEpicSummary = (): UpdateEpicSummary => {
	const apolloClient = useApolloClient();
	const isCompanyManaged = useIsCMPBacklog();
	const [, { updateEpicAttributes }] = useEpicsStoreActions();

	const updateEpicSummary = (boardId: number, issueId: string, summary: string) => {
		if (fg('jsw_epic_panel_paginated_fetch')) {
			updateEpicAttributes(issueId, { name: summary });
			return;
		}

		const board = apolloClient.readQuery<EpicQueryResponse, EpicQueryVariables>({
			query: EPIC_QUERY,
			variables: {
				boardId,
				isCompanyManaged,
			},
		});

		if (board?.boardScope?.issueParents) {
			const issueParents = board.boardScope.issueParents.map((epic) => {
				if (epic.id === issueId) {
					return { ...epic, summary };
				}
				return epic;
			});
			apolloClient.writeQuery({
				query: EPIC_QUERY,
				variables: {
					boardId,
					isCompanyManaged,
				},
				data: {
					boardScope: {
						...board.boardScope,
						issueParents: [...issueParents],
					},
				},
			});
		}
	};

	return {
		updateEpicSummary,
	};
};
